import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/Checkbox';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { CanvaDesignForUI } from '@/types/canva';

/**
 * CanvaDesignCard Component
 *
 * Single design card with thumbnail, title, metadata, and selection checkbox
 */

interface CanvaDesignCardProps {
  design: CanvaDesignForUI;
  isSelected: boolean;
  onToggleSelect: () => void;
}

export const CanvaDesignCard = ({
  design,
  isSelected,
  onToggleSelect,
}: CanvaDesignCardProps) => {
  const { t } = useTranslation('common');
  const { formatRelativeDate } = useTimezone();

  return (
    <div
      className={`
        group relative rounded-lg border-2 transition-all duration-200
        overflow-hidden cursor-pointer
        hover:border-primary-300 hover:shadow-md
        ${
          isSelected
            ? 'border-primary-500 shadow-md scale-[0.98]'
            : 'border-gray-200 hover:scale-[1.02]'
        }
      `}
      onClick={onToggleSelect}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onToggleSelect();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${isSelected ? t('deselect') : t('select')} ${design.title}`}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-gray-100 relative overflow-hidden">
        <img
          src={design.thumbnailUrl}
          alt={design.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Checkbox overlay */}
        <div
          className="absolute top-2 right-2 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            className="bg-white shadow-sm"
          />
        </div>

        {/* Badges */}
        <div className="absolute bottom-2 left-2 flex gap-2">
          {design.isMultiPage && (
            <span className="px-2 py-1 bg-black/70 text-white text-xs font-medium rounded flex items-center gap-1">
              <Icon name={ICONS.FILE} size="sm" color="text-white" ariaHidden />
              {design.pageCount} {t('pages')}
            </span>
          )}
          {design.hasVideo && (
            <span className="px-2 py-1 bg-black/70 text-white text-xs font-medium rounded flex items-center gap-1">
              <Icon name={ICONS.VIDEO} size="sm" color="text-white" ariaHidden />
              {t('video')}
            </span>
          )}
          {design.hasAnimation && (
            <span className="px-2 py-1 bg-black/70 text-white text-xs font-medium rounded flex items-center gap-1">
              <Icon name={ICONS.SPARKLES} size="sm" color="text-white" ariaHidden />
              {t('animated')}
            </span>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-3 bg-white">
        <h4 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">
          {design.title}
        </h4>
        <p className="text-xs text-gray-500">
          {formatRelativeDate(design.updatedAt)}
        </p>
      </div>

      {/* Selection indicator overlay */}
      {isSelected && (
        <div className="absolute inset-0 bg-primary-500/10 pointer-events-none" />
      )}
    </div>
  );
};
